/**
 * Contorno na câmera de quem está falando.
 *
 * O aviso `activeSpeaker` do kit só troca quando a pessoa da vez muda e
 * não apaga quando ela para. A borda ficava presa numa câmera só.
 * Aqui o volume sai da própria faixa de áudio de cada câmera: passa do
 * limiar, acende; fica em silêncio, apaga. Várias pessoas podem acender
 * ao mesmo tempo.
 *
 * O tile vive dentro de shadow roots do kit. O estilo é injetado lá,
 * porque CSS global não entra, e o vídeo cobre a borda do host.
 */

export const SPEAKING_ATTR = "data-constela-speaking";

const STYLE_ATTR = "data-constela-speaking-style";
const LAYER_STYLE_ATTR = "data-constela-speaking-layer";

/** RMS do sinal. Abaixo disso é sala quieta, não voz. */
export const SPEAKING_LEVEL = 0.025;

/** Segura a borda um pouco depois da frase, para não piscar entre palavras. */
export const SPEAKING_HOLD_MS = 600;

export const MENU_OPEN_ATTR = "data-constela-menu";
export const MENU_ANCESTOR_ATTR = "data-constela-menu-ancestor";

/**
 * A moldura fica acima do vídeo e abaixo do nome e do menu.
 * z-index alto cobria o menu dos três pontinhos.
 */
export const SPEAKING_TILE_CSS = `
:host([${SPEAKING_ATTR}="true"]) {
  outline: 4px solid #ffffff;
  outline-offset: -4px;
}
:host([${SPEAKING_ATTR}="true"])::after {
  content: "";
  position: absolute;
  inset: 0;
  border: 4px solid #ffffff;
  border-radius: inherit;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.35), inset 0 0 14px rgba(255, 255, 255, 0.45);
  pointer-events: none;
  z-index: 1;
}
:host([${SPEAKING_ATTR}="true"]) rtk-name-tag,
:host([${SPEAKING_ATTR}="true"]) rtk-menu,
:host([${SPEAKING_ATTR}="true"]) .pinned-icon,
:host([${SPEAKING_ATTR}="true"]) .network-container {
  z-index: 6;
}
`;

/** O menu aberto fica acima da câmera de quem está falando. */
export const SPEAKING_LAYER_CSS = `
rtk-participant[${MENU_OPEN_ATTR}="open"],
rtk-participant-tile[${MENU_OPEN_ATTR}="open"] {
  position: relative;
  z-index: 80;
  overflow: visible;
}
[${MENU_ANCESTOR_ATTR}="true"] {
  position: relative;
  z-index: 80;
  overflow: visible;
}
rtk-participant-tile[${SPEAKING_ATTR}="true"] {
  position: relative;
  z-index: 1;
}
`;

const TILE_TAGS = new Set(["rtk-participant-tile", "dyte-participant-tile"]);

type TileElement = HTMLElement & {
  participant?: { id?: string; audioEnabled?: boolean; audioTrack?: MediaStreamTrack | null };
};

export function tilePeerId(el: HTMLElement): string {
  const id = (el as TileElement).participant?.id;
  return typeof id === "string" ? id : "";
}

export function nextSpeaking(
  wasSpeaking: boolean,
  level: number,
  silentSince: number | null,
  now: number,
): { speaking: boolean; silentSince: number | null } {
  if (level >= SPEAKING_LEVEL) return { speaking: true, silentSince: null };
  if (!wasSpeaking) return { speaking: false, silentSince: null };
  const since = silentSince ?? now;
  if (now - since >= SPEAKING_HOLD_MS) return { speaking: false, silentSince: null };
  return { speaking: true, silentSince: since };
}

function ensureTileStyle(el: HTMLElement): void {
  const shadow = el.shadowRoot;
  if (!shadow || shadow.querySelector(`style[${STYLE_ATTR}]`)) return;
  const style = document.createElement("style");
  style.setAttribute(STYLE_ATTR, "true");
  style.textContent = SPEAKING_TILE_CSS;
  shadow.appendChild(style);
}

function ensureLayerStyle(root: ParentNode): void {
  if (root.querySelector(`style[${LAYER_STYLE_ATTR}]`)) return;
  const style = document.createElement("style");
  style.setAttribute(LAYER_STYLE_ATTR, "true");
  style.textContent = SPEAKING_LAYER_CSS;
  root.appendChild(style);
}

export function menuListIsOpen(menu: HTMLElement): boolean {
  const list = menu.shadowRoot?.querySelector("#menu-list");
  return list instanceof HTMLElement && list.style.display === "block";
}

function shadowContainsOpenMenu(root: ParentNode | null | undefined): boolean {
  if (!root) return false;
  const menus = root.querySelectorAll("rtk-menu");
  for (let i = 0; i < menus.length; i += 1) {
    const menu = menus[i];
    if (menu instanceof HTMLElement && menuListIsOpen(menu)) return true;
    if (menu instanceof HTMLElement && shadowContainsOpenMenu(menu.shadowRoot)) return true;
  }
  const nested = root.querySelectorAll("*");
  for (let i = 0; i < nested.length; i += 1) {
    const el = nested[i];
    if (el instanceof HTMLElement && el.shadowRoot && shadowContainsOpenMenu(el.shadowRoot)) {
      return true;
    }
  }
  return false;
}

/**
 * Marca a linha ou a câmera cujo menu dos três pontinhos está aberto,
 * e os painéis em volta, para a moldura de fala não cobrir o menu.
 */
export function syncOpenMenus(root: ParentNode): void {
  if (root instanceof Document || root instanceof ShadowRoot) {
    ensureLayerStyle(root);
  } else if (root instanceof HTMLElement) {
    ensureLayerStyle(root);
  }
  const openAncestors = new Set<HTMLElement>();
  walkTree(
    root,
    (el) => {
      const tag = el.localName;
      if (tag !== "rtk-participant" && tag !== "rtk-participant-tile") return;
      if (!shadowContainsOpenMenu(el.shadowRoot)) {
        el.removeAttribute(MENU_OPEN_ATTR);
        return;
      }
      el.setAttribute(MENU_OPEN_ATTR, "open");
      let parent: Node | null = el.parentNode;
      while (parent && parent !== root) {
        if (parent instanceof ShadowRoot) {
          ensureLayerStyle(parent);
          const host = parent.host;
          if (host instanceof HTMLElement) {
            host.setAttribute(MENU_ANCESTOR_ATTR, "true");
            openAncestors.add(host);
            parent = host.parentNode;
            continue;
          }
        }
        parent = parent instanceof HTMLElement ? parent.parentNode : null;
      }
    },
    ensureLayerStyle,
  );
  walkTree(root, (el) => {
    if (el.hasAttribute(MENU_ANCESTOR_ATTR) && !openAncestors.has(el)) {
      el.removeAttribute(MENU_ANCESTOR_ATTR);
    }
  });
}

function walkTree(root: ParentNode, onElement: (el: HTMLElement) => void, onShadow?: (s: ShadowRoot) => void): void {
  const walk = (node: ParentNode) => {
    const elements =
      node instanceof Element
        ? [node, ...Array.from(node.querySelectorAll("*"))]
        : Array.from(node.querySelectorAll("*"));
    for (const el of elements) {
      if (el instanceof HTMLElement) onElement(el);
      const shadow = (el as HTMLElement).shadowRoot;
      if (shadow) {
        onShadow?.(shadow);
        walk(shadow);
      }
    }
  };
  walk(root);
}

export function markSpeakingTiles(
  root: ParentNode,
  peerIds: string | ReadonlySet<string>,
  onShadow?: (s: ShadowRoot) => void,
): number {
  const ids = typeof peerIds === "string" ? null : peerIds;
  const one = typeof peerIds === "string" ? peerIds : "";
  let marked = 0;
  walkTree(
    root,
    (el) => {
      if (!TILE_TAGS.has(el.tagName.toLowerCase())) return;
      ensureTileStyle(el);
      const id = tilePeerId(el);
      const speaking = ids ? ids.has(id) : one.length > 0 && id === one;
      if (speaking) {
        if (el.getAttribute(SPEAKING_ATTR) !== "true") el.setAttribute(SPEAKING_ATTR, "true");
        marked += 1;
      } else if (el.hasAttribute(SPEAKING_ATTR)) {
        el.removeAttribute(SPEAKING_ATTR);
      }
    },
    onShadow,
  );
  return marked;
}

type SpeakingState = { speaking: boolean; silentSince: number | null };

type Meter = {
  track: MediaStreamTrack;
  source: MediaStreamAudioSourceNode;
  analyser: AnalyserNode;
  gain: GainNode;
};

function rms(analyser: AnalyserNode, buf: Uint8Array): number {
  analyser.getByteTimeDomainData(buf);
  let sum = 0;
  for (let i = 0; i < buf.length; i += 1) {
    const x = (buf[i] - 128) / 128;
    sum += x * x;
  }
  return Math.sqrt(sum / buf.length);
}

/**
 * Liga o contorno e acompanha o volume das câmeras que estão na tela.
 */
export function enableSpeakingOutline(rootEl: HTMLElement | null): () => void {
  if (!rootEl || typeof MutationObserver === "undefined" || typeof AudioContext === "undefined") {
    return () => undefined;
  }

  const AudioCtx = AudioContext;
  let ctx: AudioContext | null = null;
  const meters = new Map<string, Meter>();
  const states = new Map<string, SpeakingState>();
  const buf = new Uint8Array(512);
  let timer: number | undefined;
  const observedShadows = new WeakSet<ShadowRoot>();
  const observers: MutationObserver[] = [];

  const watchShadow = (shadow: ShadowRoot) => {
    if (observedShadows.has(shadow)) return;
    observedShadows.add(shadow);
    const observer = new MutationObserver(schedule);
    observer.observe(shadow, { childList: true, subtree: true });
    observers.push(observer);
  };

  const levelOf = (track: MediaStreamTrack): number => {
    if (!ctx) ctx = new AudioCtx();
    if (ctx.state === "suspended") void ctx.resume();
    let meter = meters.get(track.id);
    if (!meter || meter.track !== track) {
      meter?.source.disconnect();
      meter?.gain.disconnect();
      const source = ctx.createMediaStreamSource(new MediaStream([track]));
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      source.connect(analyser);
      analyser.connect(gain);
      gain.connect(ctx.destination);
      meter = { track, source, analyser, gain };
      meters.set(track.id, meter);
    }
    return rms(meter.analyser, buf);
  };

  const apply = () => {
    timer = undefined;
    if (document.hidden) {
      schedule();
      return;
    }
    const now = Date.now();
    const speaking = new Set<string>();
    const seen = new Set<string>();
    walkTree(rootEl, (el) => {
      if (!TILE_TAGS.has(el.tagName.toLowerCase())) return;
      const tile = el as TileElement;
      const id = tilePeerId(el);
      if (!id || seen.has(id)) return;
      seen.add(id);
      const track = tile.participant?.audioEnabled ? tile.participant.audioTrack : null;
      let level = 0;
      if (track && track.readyState === "live") {
        try {
          level = levelOf(track);
        } catch {
          level = 0;
        }
      }
      const prev = states.get(id) || { speaking: false, silentSince: null };
      const next = nextSpeaking(prev.speaking, level, prev.silentSince, now);
      states.set(id, next);
      if (next.speaking) speaking.add(id);
    });
    Array.from(states.keys()).forEach((id) => {
      if (!seen.has(id)) states.delete(id);
    });
    markSpeakingTiles(rootEl, speaking, watchShadow);
    syncOpenMenus(rootEl);
    schedule();
  };

  function schedule() {
    if (timer !== undefined) return;
    timer = window.setTimeout(apply, 200);
  }

  const rootObserver = new MutationObserver(schedule);
  rootObserver.observe(rootEl, { childList: true, subtree: true });
  observers.push(rootObserver);
  apply();

  return () => {
    observers.forEach((observer) => observer.disconnect());
    if (timer !== undefined) window.clearTimeout(timer);
    meters.forEach((meter) => {
      meter.source.disconnect();
      meter.gain.disconnect();
    });
    meters.clear();
    void ctx?.close();
    markSpeakingTiles(rootEl, "");
  };
}
